import { Badge, Button, Icon } from '@kontentino/ui';
import { faBuilding } from '@fortawesome/pro-regular-svg-icons/faBuilding';
import { faShieldCheck } from '@fortawesome/pro-regular-svg-icons/faShieldCheck';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons/faTriangleExclamation';
import clsx from 'clsx';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface SelectFacebookTokenTypeProps {
  onSelectPersonal: () => void;
  onSelectObo: () => void;
}

const SelectFacebookTokenType = ({
  onSelectPersonal,
  onSelectObo,
}: SelectFacebookTokenTypeProps) => {
  const { t } = useTranslation();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isOtherOpen, setIsOtherOpen] = useState(true);

  const handleSelectPersonal = () => {
    if (isRedirecting) {
      return;
    }
    setIsRedirecting(true);
    onSelectPersonal();
  };

  const handleSelectObo = () => {
    if (isRedirecting) {
      return;
    }
    setIsRedirecting(true);
    onSelectObo();
  };

  return (
    <div className="tw-flex tw-flex-col tw-pt-5">
      <div className="tw-relative tw-rounded-xl tw-border-[1.5px] tw-border-primary-100 tw-bg-gradient-to-b tw-from-primary-10/40 tw-to-white tw-p-[18px]">
        <Badge
          variant="primary"
          className="tw-absolute -tw-top-[10px] tw-left-[18px] tw-uppercase"
        >
          {t('newSocialMediaProfile.fbTokenType.recommended')}
        </Badge>
        <div className="tw-flex tw-items-start tw-gap-3">
          <div className="tw-flex tw-h-[38px] tw-w-[38px] tw-flex-none tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-primary-10 tw-text-primary-100">
            <Icon icon={faBuilding} size="md" />
          </div>
          <div>
            <div className="tw-text-md tw-font-bold tw-text-grayscale-180">
              {t('newSocialMediaProfile.oboTokenType.title')}
            </div>
            <div className="tw-mt-1 tw-text-xs tw-leading-relaxed tw-text-grayscale-140">
              {t('newSocialMediaProfile.oboTokenType.description')}
            </div>
            <span className="tw-mt-[10px] tw-inline-flex tw-items-center tw-gap-[6px] tw-rounded-md tw-bg-success-10 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-text-success-140">
              <Icon icon={faShieldCheck} size="sm" />
              {t('newSocialMediaProfile.oboTokenType.safeConnect')}
            </span>
          </div>
        </div>
        <Button
          variant="primary"
          className="tw-mt-4 tw-w-full"
          iconAfter={<Icon icon={faArrowRight} />}
          disabled={isRedirecting}
          isLoading={isRedirecting}
          data-cy="add-profile_fb-token-type_obo"
          data-testid="add-profile_fb-token-type_obo"
          onClick={handleSelectObo}
        >
          {t('continue')}
        </Button>
      </div>

      <div className="tw-mt-4 tw-border-t tw-border-grayscale-20">
        <button
          type="button"
          className="tw-flex tw-w-full tw-items-center tw-justify-between tw-bg-transparent tw-py-3 tw-text-sm tw-font-semibold tw-text-grayscale-140 hover:tw-text-grayscale-180"
          onClick={() => setIsOtherOpen((prev) => !prev)}
          aria-expanded={isOtherOpen}
          data-cy="add-profile_fb-token-type_other-toggle"
        >
          {t('newSocialMediaProfile.fbTokenType.otherWaysToConnect')}
          <Icon
            icon={faChevronDown}
            size="sm"
            className={clsx('tw-transition-transform', {
              'tw-rotate-180': isOtherOpen,
            })}
          />
        </button>
        {isOtherOpen && (
          <div
            role="button"
            tabIndex={0}
            className="tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-border-grayscale-20 tw-p-[14px] tw-transition-colors hover:tw-border-grayscale-30 hover:tw-bg-grayscale-5 aria-disabled:tw-cursor-wait"
            aria-disabled={isRedirecting}
            data-cy="add-profile_fb-token-type_personal"
            data-testid="add-profile_fb-token-type_personal"
            onClick={handleSelectPersonal}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSelectPersonal();
              }
            }}
          >
            <div className="tw-flex tw-h-[34px] tw-w-[34px] tw-flex-none tw-items-center tw-justify-center tw-rounded-[10px] tw-bg-grayscale-10 tw-text-grayscale-140">
              <Icon icon={faUser} size="sm" />
            </div>
            <div className="tw-flex-1">
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <span className="tw-text-sm tw-font-semibold tw-text-grayscale-180">
                  {t('newSocialMediaProfile.fbTokenType.personalTitle')}
                </span>
                <Badge variant="gray" className="tw-uppercase">
                  {t('newSocialMediaProfile.fbTokenType.legacy')}
                </Badge>
                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-md tw-bg-warning-10 tw-px-[6px] tw-py-[2px] tw-text-xs tw-font-semibold tw-text-warning-140">
                  <Icon icon={faTriangleExclamation} size="sm" />
                  {t('newSocialMediaProfile.fbTokenType.canExpire')}
                </span>
              </div>
              <div className="tw-mt-1 tw-text-xs tw-leading-relaxed tw-text-grayscale-140">
                {t('newSocialMediaProfile.fbTokenType.personalDescription')}
              </div>
            </div>
            <Icon
              icon={faChevronRight}
              size="sm"
              className="tw-mt-1 tw-flex-none tw-text-grayscale-90"
            />
          </div>
        )}
      </div>

      {isRedirecting &&
        createPortal(
          <div
            className="tw-fixed tw-inset-0 tw-z-[9999] tw-cursor-wait"
            aria-hidden="true"
          />,
          document.body,
        )}
    </div>
  );
};

export default SelectFacebookTokenType;
